import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Send, CheckCircle, AlertCircle, Mail } from 'lucide-react';
import { supabase, getSupabaseUrl } from '../../supabase/client';
import { formatDate, formatDateTime } from '../../utils/timeUtils';

const testScenes = [
  { id: 'package_purchase', name: '刷题包订购支付成功', desc: '模拟支付成功后发送站内信' },
  { id: 'password_reset', name: '账号重置密码成功', desc: '模拟密码重置后发送站内信' },
  { id: 'bind_teacher', name: '学员绑定教师成功', desc: '模拟绑定教师后发送站内信' },
  { id: 'order_refund', name: '订单退款/过期', desc: '模拟订单退款后发送站内信' },
  { id: 'package_expire', name: '刷题包到期提醒', desc: '模拟刷题包到期前发送站内信' },
];

export default function MessageTest() {
  const [results, setResults] = useState<Record<string, { success?: boolean; message?: string; loading?: boolean }>>({});
  const [targetUserId, setTargetUserId] = useState('');

  const testScene = async (sceneType: string) => {
    setResults(prev => ({ ...prev, [sceneType]: { loading: true } }));

    try {
      const session = (await supabase.auth.getSession()).data.session;
      if (!session) {
        setResults(prev => ({ ...prev, [sceneType]: { success: false, message: '请先登录' } }));
        return;
      }

      const userId = targetUserId || session.user.id;
      const variables: Record<string, string> = {
        username: '测试用户',
        orderNo: 'ORD' + Date.now(),
        package_name: '测试刷题包',
        expire_date: formatDate(new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString()),
        teacher_name: '测试教师',
        status: '已退款',
        refundAmount: '99.00',
        refundTime: formatDateTime(new Date().toISOString()),
        resetTime: formatDateTime(new Date().toISOString()),
      };

      const response = await fetch(`${getSupabaseUrl()}/functions/v1/message-service`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${session.access_token}`,
        },
        body: JSON.stringify({
          action: 'messages_trigger_scene',
          sceneType,
          targetUserId: userId,
          variables,
        }),
      });

      const result = await response.json();
      setResults(prev => ({
        ...prev,
        [sceneType]: {
          success: result.success,
          message: result.message || result.error,
        },
      }));
    } catch (error: any) {
      setResults(prev => ({
        ...prev,
        [sceneType]: {
          success: false,
          message: error.message || '请求失败',
        },
      }));
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-gray-800 flex items-center gap-2">
          <Mail className="w-6 h-6 text-blue-600" />
          站内信场景测试
        </h1>
      </div>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-white rounded-xl shadow-sm border border-gray-100 p-6"
      >
        <div className="mb-6">
          <label className="block text-sm font-medium text-gray-700 mb-2">目标用户ID（可选，默认发送给自己）</label>
          <input
            type="text"
            value={targetUserId}
            onChange={(e) => setTargetUserId(e.target.value)}
            placeholder="输入用户UUID，留空则发送给自己"
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none"
          />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {testScenes.map((scene) => (
            <div
              key={scene.id}
              className="border border-gray-200 rounded-lg p-4 hover:border-blue-300 transition-colors"
            >
              <div className="flex items-start justify-between">
                <div>
                  <h3 className="font-semibold text-gray-800">{scene.name}</h3>
                  <p className="text-sm text-gray-500 mt-1">{scene.desc}</p>
                </div>
                <button
                  onClick={() => testScene(scene.id)}
                  disabled={results[scene.id]?.loading}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 flex items-center gap-2"
                >
                  {results[scene.id]?.loading ? (
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  ) : (
                    <Send className="w-4 h-4" />
                  )}
                  测试
                </button>
              </div>

              {results[scene.id] && !results[scene.id]?.loading && (
                <div className={`mt-3 p-3 rounded-lg flex items-center gap-2 ${
                  results[scene.id]?.success ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'
                }`}>
                  {results[scene.id]?.success ? (
                    <CheckCircle className="w-4 h-4" />
                  ) : (
                    <AlertCircle className="w-4 h-4" />
                  )}
                  <span className="text-sm">{results[scene.id]?.message}</span>
                </div>
              )}
            </div>
          ))}
        </div>
      </motion.div>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
        className="bg-amber-50 rounded-xl border border-amber-200 p-6"
      >
        <h3 className="font-semibold text-amber-800 mb-2">测试说明</h3>
        <ul className="text-sm text-amber-700 space-y-1 list-disc list-inside">
          <li>点击「测试」按钮会触发对应场景的站内信发送</li>
          <li>消息会写入 system_messages 表，并为当前用户创建 user_message_reads 记录</li>
          <li>测试后请到「站内信管理」页面查看发送的消息</li>
          <li>或到学员/教师中心的「我的站内信」卡片查看未读数量</li>
          <li>如需测试其他用户，请填写目标用户ID（UUID格式）</li>
        </ul>
      </motion.div>
    </div>
  );
}
